import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { debounce } from 'lodash';
import { CcIcon } from '@utp/components-react';
import { ButtonPrerequisites, Container } from './styled';
import ContainerProcedure from '@/presentation/modules/procedures/components/ContainerProcedure';
import { BackButton } from '@/presentation/modules/procedures/components';
import Spinner from '@/presentation/components/Spinner';
import TextHelper from '@/core/helpers/TextHelper';
import ReusableButton from '@/presentation/reusables/Button';
import FileDownloader from './components/FileDownloader';
import warningAlertIcon from '../../../../../../assets/icons/warning-alert.svg';
import iconEmptyState from '../../../../../../assets/icons/emptyState.svg';
import eyeIcon from '../../../../../../assets/icons/eye-icon.svg';
import eyeCloseIcon from '../../../../../../assets/icons/eye-close-icon.svg';
import { Icon } from '@/presentation/components';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';

interface Course {
  name: string;
  courseId: string;
  code: string;
  credits: number;
  prerequisites: { code: string; name: string }[];
  type: string;
  status: string;
  hours: number;
}

interface Cycle {
  term: string;
  courses: Course[];
  mandatoryCourseCount: number;
}

interface Props {
  data: Cycle[];
  loadEquivalences: (courseId: string, courseCode: string[]) => void;
  equivalences: any;
  isLoadingEquivalences: boolean;
  setSelectedCoursePrerequisites: (prerequisites: string[]) => void;
  selectedCoursePrerequisites: string[];
  setMainSelectedCoursePrerequisites: (courseId: string) => void;
  mainSelectedCoursePrerequisites: string | null;
  handlerBack: () => void;
  isLoadingPlanProgressDetail: boolean;
  academicPlan: any;
  fullName: string;
  program: string;
  isProcessingPlanProgressDetail: boolean;
}

const AdvanceCurriculumCoursesView: React.FC<Props> = ({
  data,
  loadEquivalences,
  equivalences,
  isLoadingEquivalences,
  setSelectedCoursePrerequisites,
  selectedCoursePrerequisites,
  setMainSelectedCoursePrerequisites,
  mainSelectedCoursePrerequisites,
  handlerBack,
  isLoadingPlanProgressDetail,
  academicPlan,
  fullName,
  program,
  isProcessingPlanProgressDetail
}) => {
  const [currentCycleIndex, setCurrentCycleIndex] = useState<number>(0);
  const [columns, setColumns] = useState<number>(1);
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);

  const lastWindowWidth = useRef(window.innerWidth);
  const lastWindowHeight = useRef(window.innerHeight);

  const getColumnsByWidth = (width: any) => {
    if (width >= 1280) return 4;
    if (width >= 992) return 3;
    if (width >= 640) return 2;
    return 1;
  };

  const updateColumns = useCallback(() => {
    const newColumns = getColumnsByWidth(window.innerWidth);
    setColumns(newColumns);
  }, []);

  const handleResize = useCallback(
    debounce(() => {
      if (
        window.innerWidth !== lastWindowWidth.current ||
        window.innerHeight !== lastWindowHeight.current
      ) {
        updateColumns();
        lastWindowWidth.current = window.innerWidth;
        lastWindowHeight.current = window.innerHeight;
      }
    }, 200),
    [updateColumns]
  );

  useEffect(() => {
    updateColumns();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [handleResize, updateColumns]);

  const handleCycleChange = (direction: 'prev' | 'next', e: any) => {
    GoogleAnalytics.writeLog(
      `portal-course-${direction}ButtonProgressByCourses`,
      'course'
    );
    const increment = direction === 'next' ? columns : -columns;
    e.target.blur();
    e.target.parentNode.blur();

    setCurrentCycleIndex((prevIndex) => {
      const newIndex = prevIndex + increment;
      const minIndex = 0;
      const maxIndex = data.length;
      return Math.min(Math.max(newIndex, minIndex), maxIndex);
    });
  };

  const handleExpandCourse = (courseId: string, courseCode: any) => {
    if (expandedCourseId === courseId) {
      setExpandedCourseId(null);
      setSelectedCoursePrerequisites([]);
      setMainSelectedCoursePrerequisites('');
    } else {
      setExpandedCourseId(courseId);
      const codes = courseCode.map((item: any) => item.code);
      loadEquivalences(courseId, codes);
    }
  };

  const getClassForStatus = (type: string) => {
    switch (type) {
      case 'APROBADO':
        return 'approved';
      case 'CONVALIDADO':
        return 'validated';
      case 'EN CURSO':
        return 'inProgress';
      case 'PENDIENTE':
        return 'pending';
      default:
        return '';
    }
  };
  const getLetterForStatus = (type: string) => {
    switch (type) {
      case 'APROBADO':
        return 'A';
      case 'CONVALIDADO':
        return 'C';
      case 'EN CURSO':
        return 'E';
      case 'PENDIENTE':
        return 'P';
      default:
        return '';
    }
  };

  const getClassForCourse = (courseId: string) => {
    if (selectedCoursePrerequisites.length === 0) {
      return '';
    }
    if (courseId === mainSelectedCoursePrerequisites) {
      return 'selected';
    }
    if (selectedCoursePrerequisites.includes(courseId)) {
      return 'prerequisite';
    }
    return 'disabled';
  };
  const currentCycles = useMemo(
    () => data.slice(currentCycleIndex, currentCycleIndex + columns),
    [data, currentCycleIndex, columns]
  );

  const isLeftArrowDisabled = currentCycleIndex === 0;
  const isRightArrowDisabled = currentCycleIndex >= data.length - columns;
  const headerView = () => (
    <div className="navigation">
      <div className="states">
        <div className="signature-states">Leyenda:</div>
        <div className="legend-big-group">
          <div className="legend-group-640">
            <div className="legend-group">
              <span className="legend">
                <span className="icon approved">A</span>
                <span className="text">Aprobado</span>
              </span>
              <span className="legend">
                <span className="icon validated">C</span>
                <span className="text">Convalidado</span>
              </span>
              <span className="legend">
                <span className="icon inProgress">E</span>
                <span className="text">En curso</span>
              </span>
            </div>
            <div className="legend-group">
              <span className="legend">
                <span className="icon pending">P</span>
                <span className="text">Pendiente</span>
              </span>
              <span className="legend">
                <span className="icon required" />
                <span className="text">Curso obligatorio</span>
              </span>
            </div>
          </div>
          <span className="legend">
            <span className="icon elective" />
            <span className="text">Curso electivo</span>
          </span>
        </div>
      </div>
      <div className="buttons-cv">
        <FileDownloader
          academicPlan={academicPlan}
          data={data}
          fullName={fullName}
          program={program}
          disabled={isProcessingPlanProgressDetail}
        />

        <div className="optional-button" data-testid="optional-button">
          <ReusableButton
            data-testid="left-button"
            style={{ gap: 0, height: '44px' }}
            text=""
            iconPrepend="chevron-left"
            onClick={(e) => handleCycleChange('prev', e)}
            disabled={isLeftArrowDisabled}
          />
          <ReusableButton
            data-testid="right-button"
            style={{ gap: 0, height: '44px' }}
            text=""
            iconAppend="chevron-right"
            onClick={(e) => handleCycleChange('next', e)}
            disabled={isRightArrowDisabled}
          />
        </div>
      </div>
    </div>
  );

  if (isLoadingPlanProgressDetail) {
    return (
      <ContainerProcedure>
        <BackButton onClick={handlerBack} />
        <Container>
          <div className="title">Avance por cursos</div>

          <div className="alert-info-courses">
            <div className="alert-info-courses-icon">
              <img src={warningAlertIcon} alt="warningAlertIcon" />
            </div>
            <div>
              <ul>
                <li>
                  Ten en cuenta que en el ciclo de verano la cantidad de horas
                  semanales se duplica porque tiene la mitad de semanas que un
                  ciclo regular.
                </li>
                <li>
                  Las asignaturas de nivelación y los cursos complementos no se
                  consideran en el cálculo de las condiciones de egreso.
                </li>
              </ul>
            </div>
          </div>

          <div className="grid">
            {headerView()}
            <Spinner height={300} colorText="#161D1F" />
          </div>
        </Container>
      </ContainerProcedure>
    );
  }
  if (!currentCycles.length) {
    return (
      <ContainerProcedure>
        <BackButton onClick={handlerBack} />
        <Container>
          <div className="title">Avance por cursos</div>

          <div className="alert-info-courses">
            <div className="alert-info-courses-icon">
              <img src={warningAlertIcon} alt="warningAlertIcon" />
            </div>
            <div>
              <ul>
                <li>
                  Ten en cuenta que en el ciclo de verano la cantidad de horas
                  semanales se duplica porque tiene la mitad de semanas que un
                  ciclo regular.
                </li>
                <li>
                  Las asignaturas de nivelación y los cursos complementos no se
                  consideran en el cálculo de las condiciones de egreso.
                </li>
              </ul>
            </div>
          </div>

          <div className="grid">
            {headerView()}
            <div className="empty-data-container">
              <img src={iconEmptyState} alt="" className="image" />
              <div className="text">
                Actualmente no hay datos disponibles para mostrar. Actualiza
                esta página en unos minutos.
              </div>
            </div>
          </div>
        </Container>
      </ContainerProcedure>
    );
  }

  return (
    <ContainerProcedure>
      <BackButton onClick={handlerBack} />
      <Container>
        <div className="title">Avance por cursos</div>

        <div className="alert-info-courses">
          <div className="alert-info-courses-icon">
            <img src={warningAlertIcon} alt="warningAlertIcon" />
          </div>
          <div>
            <ul>
              <li>
                Ten en cuenta que en el ciclo de verano la cantidad de horas
                semanales se duplica porque tiene la mitad de semanas que un
                ciclo regular.
              </li>
              <li>
                Las asignaturas de nivelación y los cursos complementos no se
                consideran en el cálculo de las condiciones de egreso.
              </li>
            </ul>
          </div>
        </div>

        <div className="grid">
          {headerView()}
          <div className="content">
            {currentCycles.map((cycle, indexCycle) => (
              <div key={indexCycle} className="cycle" data-testid="cycle">
                <div className="cycle-card">
                  <div className="cycle-card-title">{cycle.term}</div>

                  <div className="cycle-card-subtitle">
                    Total de créditos obligatorios: {cycle.mandatoryCourseCount}
                  </div>
                </div>
                <div className="courses">
                  {cycle.courses.map((course, index) => (
                    <div
                      key={index}
                      className={`course-card ${getClassForCourse(
                        course.code
                      )}`}
                    >
                      <div className="card-content">
                        <div className="card-legend">
                          <div className="legend-text">
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                            >
                              <div>
                                <div style={{ display: 'flex' }}>
                                  <div
                                    className={`legend-icon icon ${getClassForStatus(
                                      course.status
                                    )}`}
                                  >
                                    {getLetterForStatus(course.status)}
                                  </div>
                                  <div>
                                    <span
                                      className={`icon  ${
                                        course.type === 'O'
                                          ? 'required'
                                          : 'elective'
                                      }`}
                                    />
                                  </div>
                                </div>
                                <div className="legend-title">
                                  {TextHelper.fixRomans(
                                    TextHelper.capitalize(
                                      course.name,
                                      false,
                                      true
                                    )
                                  )}
                                </div>
                              </div>
                              <div className="card-button">
                                <CcIcon
                                  data-testid="expand-button"
                                  name={
                                    expandedCourseId === course.courseId
                                      ? 'chevron-up'
                                      : 'chevron-down'
                                  }
                                  onClick={() =>
                                    handleExpandCourse(
                                      course.courseId,
                                      course.prerequisites
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div className="divider" />
                            <div className="legend-content">
                              <div className="legend-content-text">
                                Código: {course.code}
                              </div>
                              <div className="legend-content-text2">
                                Horas Semanales: {course.hours.toFixed(2)}
                              </div>

                              <div className="legend-content-text-3">
                                Créditos: {course.credits.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {expandedCourseId === course.courseId &&
                        course.prerequisites.length > 0 && (
                          <div className="prerequisite">
                            <div>Pre-Requisitos</div>
                            {isLoadingEquivalences && (
                              <Spinner height={80} colorText="#161D1F" />
                            )}
                            {equivalences?.preRequisites.map(
                              (prerequisite: any) => (
                                <li key={prerequisite.code}>
                                  {prerequisite.code} {' - '}
                                  {TextHelper.fixRomans(
                                    TextHelper.capitalize(
                                      prerequisite.name,
                                      false,
                                      true
                                    )
                                  )}
                                </li>
                              )
                            )}
                          </div>
                        )}
                      {expandedCourseId === course.courseId && (
                        <div className="extra-info">
                          {course.prerequisites.length > 0 ? (
                            <div className="show-prerequisite-button">
                              <ButtonPrerequisites
                                data-testid="show-prerequisite-button"
                                onClick={(e: any) => {
                                  if (
                                    course.code ===
                                    mainSelectedCoursePrerequisites
                                  ) {
                                    setMainSelectedCoursePrerequisites('');
                                    setSelectedCoursePrerequisites([]);
                                    e.target.blur();
                                    return;
                                  }
                                  setMainSelectedCoursePrerequisites(
                                    course.code
                                  );
                                  setSelectedCoursePrerequisites(
                                    equivalences.courseMatch.map(
                                      (prerequisite: any) => prerequisite.course
                                    )
                                  );
                                  e.target.blur();
                                }}
                              >
                                <Icon customized maxWidth="13.33px">
                                  {mainSelectedCoursePrerequisites ===
                                  course.code ? (
                                    <img src={eyeCloseIcon} alt="other" />
                                  ) : (
                                    <img src={eyeIcon} alt="other" />
                                  )}
                                </Icon>
                                {mainSelectedCoursePrerequisites === course.code
                                  ? 'Ocultar pre requisito'
                                  : 'Ver pre-requisitos'}
                              </ButtonPrerequisites>
                            </div>
                          ) : (
                            <div className="prerequisite">
                              Pre-Requisitos: Ninguno
                            </div>
                          )}

                          <div className="prerequisite-text">
                            Equivalencias:{' '}
                            {equivalences?.equivalences?.length || 'Ninguno'}
                          </div>

                          {isLoadingEquivalences && (
                            <Spinner height={80} colorText="#161D1F" />
                          )}
                          {equivalences &&
                            equivalences.equivalences.map(
                              (equivalence: any) => (
                                <li key={equivalence.code}>
                                  {equivalence.code}{' '}
                                  {TextHelper.fixRomans(
                                    TextHelper.capitalize(
                                      equivalence.name,
                                      false,
                                      true
                                    )
                                  )}
                                </li>
                              )
                            )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </ContainerProcedure>
  );
};

export default AdvanceCurriculumCoursesView;
